/**
 * Platform owner/super-admin REST API documentation (linked from AdminConsole). Renders the
 * generated OpenAPI 3 document (GET /rest/admin/openapi.json — see server/src/app.ts's
 * @fastify/swagger registration) with ReDoc, an industry-standard OpenAPI renderer. Admin-only:
 * gated the same way as every other /rest/admin/* call — a non-admin caller's fetch simply 403s.
 *
 * Rendered inside a same-origin(-to-the-API) <iframe> running redoc's own prebuilt standalone
 * bundle, fetched from the API server (GET /vendor/redoc-standalone.js — see app.ts) rather than
 * bundled by Parcel or loaded straight into this page:
 *   - redoc's React wrapper package ships ONLY prebuilt webpack bundles as its main/browser
 *     entries (no raw ESM source); those bundles embed internal `webpack://` source-map
 *     references that Parcel tries to resolve as real file paths and fails the build on.
 *   - The standalone bundle (redoc's own documented drop-in <script> embed) is a classic UMD
 *     build that feature-detects its environment via `typeof module === 'object'`. Referencing it
 *     through Parcel's asset graph at all — even via a raw/no-parse pipeline — still wraps the
 *     output in Parcel's own JS module runtime (`module = {...}` etc.), which makes the UMD check
 *     wrongly take the CommonJS branch and throw ("Cannot find module 'null'"). Serving it from
 *     the API as a byte-for-byte static file sidesteps Parcel entirely; the iframe then gives it
 *     a clean global scope of its own, same as any "embed a legacy third-party widget" pattern.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Loader2, FileCode2 } from 'lucide-react';
import { AdminShell, AdminMessage, ADMIN_NAVY } from './AdminShell.tsx';
import { getOpenApiSpec, getApiBaseUrl, explainAdminError } from '../../services/adminApi.ts';
import { VENDOR_ASSET_PREFIX } from '../../../../shared/src/api/routePaths.ts';

// Under /rest/vendor, from the shared constant, and not a hand-written /vendor: on the deployment
// where the app and the API share an origin only /rest, /oauth and /webhooks reach the API, so a
// /vendor url was answered with the app's own index.html — the iframe then ran a page of HTML as
// JavaScript, no Redoc global ever appeared, and this console could say only that the renderer
// failed to load. See shared/src/api/routePaths.ts.
const redocScriptUrl = `${getApiBaseUrl()}${VENDOR_ASSET_PREFIX}/redoc-standalone.js`;

const IFRAME_HTML = `<!doctype html>
<html>
<head><meta charset="utf-8"><style>html,body,#redoc-container{margin:0;padding:0;height:100%}</style></head>
<body><div id="redoc-container"></div><script src="${redocScriptUrl}"></script></body>
</html>`;

/** The redoc-standalone.js UMD bundle (loaded into the iframe's own global scope — see file header). */
interface RedocGlobal {
  Redoc: {
    init: (spec: Record<string, unknown>, options: Record<string, unknown>, target: HTMLElement | null) => void;
  };
}

interface ApiDocsConsoleProps {
  onBack: () => void;
}

const ApiDocsConsole = ({ onBack }: ApiDocsConsoleProps) => {
  const [spec, setSpec] = useState<Record<string, unknown> | null>(null); // null = loading
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    let live = true;
    getOpenApiSpec()
      .then((data) => { if (live) setSpec(data); })
      .catch((err) => { if (live) setError(explainAdminError(err, 'Could not load the API documentation just now.')); });
    return () => { live = false; };
  }, []);

  const handleIframeLoad = () => {
    if (!spec || !iframeRef.current) return;
    const frameWindow = iframeRef.current.contentWindow as (Window & Partial<RedocGlobal>) | null;
    const frameDoc = iframeRef.current.contentDocument;
    if (!frameWindow?.Redoc || !frameDoc) {
      setError('The API documentation renderer failed to load.');
      return;
    }
    frameWindow.Redoc.init(
      spec,
      { hideDownloadButton: false, expandResponses: '200,201', jsonSampleExpandLevel: 2, sortPropsAlphabetically: false, theme: { colors: { primary: { main: '#0F52E0' } } } },
      frameDoc.getElementById('redoc-container'),
    );
  };

  if (error) {
    return (
      <AdminShell title="API documentation" icon={<FileCode2 size={22} />} onBack={onBack}>
        <AdminMessage tone="error">{error}</AdminMessage>
      </AdminShell>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <div className="px-4 py-3 sm:px-8" style={{ background: ADMIN_NAVY }}>
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-white/70 transition hover:text-white">
          <ArrowLeft size={15} /> Back to admin console
        </button>
      </div>
      {!spec ? (
        <div className="flex flex-1 items-center justify-center gap-2 text-slate-400">
          <Loader2 size={20} className="animate-spin" /> Loading API documentation…
        </div>
      ) : (
        <iframe
          ref={iframeRef}
          title="REST API documentation"
          srcDoc={IFRAME_HTML}
          onLoad={handleIframeLoad}
          className="flex-1 border-0"
        />
      )}
    </div>
  );
};

export default ApiDocsConsole;
